import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft } from "lucide-react";
import { ColyAvatar } from "@/components/ColyMascot";
import Wordmark from "@/components/Wordmark";
import { LEGAL_DOCS } from "@/legal/content";

const LegalPage = () => {
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!doc) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-coly-cream text-coly-ink">
      <header className="border-b border-coly-ink/10 bg-coly-cream/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group">
            <ColyAvatar size={32} />
            <Wordmark height={26} color="#3F7556" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-extrabold text-coly-ink-soft hover:text-coly-green-deep transition-colors"
            data-testid="legal-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-6 py-12 sm:py-16" data-testid={`legal-${slug}`}>
        <article className="prose-coly">
          <ReactMarkdown
            components={{
              h1: ({ node, ...props }) => (
                <h1
                  className="text-[28px] sm:text-[36px] font-black tracking-tight leading-[1.1] mt-10 mb-3 first:mt-0"
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h2
                  className="text-[20px] sm:text-[22px] font-extrabold tracking-tight leading-tight mt-8 mb-2 text-coly-ink"
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h3
                  className="text-[16px] sm:text-[17px] font-extrabold tracking-tight mt-6 mb-2 text-coly-ink"
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p
                  className="text-[15px] sm:text-[16px] leading-[1.7] text-coly-ink-soft my-4 font-medium"
                  {...props}
                />
              ),
              a: ({ node, ...props }) => (
                <a
                  className="text-coly-green-deep font-bold underline underline-offset-4 hover:text-coly-green"
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <ul className="list-disc pl-6 my-4 space-y-1.5 text-coly-ink-soft" {...props} />
              ),
              li: ({ node, ...props }) => (
                <li className="text-[15px] sm:text-[16px] leading-[1.6] font-medium" {...props} />
              ),
              hr: () => <hr className="my-10 border-coly-ink/10" />,
              strong: ({ node, ...props }) => (
                <strong className="font-extrabold text-coly-ink" {...props} />
              ),
              code: ({ node, ...props }) => (
                <code className="bg-coly-cream2 px-1.5 py-0.5 rounded text-[13px] font-mono" {...props} />
              ),
            }}
          >
            {doc.body}
          </ReactMarkdown>
        </article>

        <footer className="mt-16 pt-8 border-t border-coly-ink/10 text-coly-ink-soft text-sm">
          <p className="font-semibold">
            Questions? Email{" "}
            <a href="mailto:support@colyapp.com" className="font-extrabold text-coly-green-deep underline underline-offset-4">
              support@colyapp.com
            </a>
          </p>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs font-bold">
            {Object.entries(LEGAL_DOCS).map(([key, d]) => (
              <Link
                key={key}
                to={`/legal/${key}`}
                className={`${
                  key === slug ? "text-coly-green-deep" : "text-coly-ink-soft hover:text-coly-ink"
                }`}
              >
                {d.title}
              </Link>
            ))}
          </div>
        </footer>
      </main>
    </div>
  );
};

export default LegalPage;
